import { useState } from "react";
import { toast } from "react-toastify";
import { api } from "../../config/api";
import { Select } from "antd";
import Modal from "react-modal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getAdmins } from "../../lib/user";
import { useMyContext } from "../../context/MyContext";
import { API_BASE_URL } from "../../config/api";

const AddTodoModal = ({ setOpenModal, openModal }) => {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { user, token } = useMyContext();
  const [data, setData] = useState({
    title: "",
    description: "",
    priority: null,
    assignedTo: null,
    date: "",
    dueDate: "",
    role: null,
  });

  const { data: users, isLoading } = useQuery(
    ["all-users"],
    async () => {
      const res = await getAdmins();
      return res?.filter((e) => e?.id !== user?.uid);
    },
    { refetchOnWindowFocus: false }
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleAddData = async (e) => {
    try {
      // Validate required fields
      if (!data.title || !data.assignedTo || !data.priority || !data.date || !data.dueDate) {
        toast.error("Please fill all required fields");
        return;
      }

      setLoading(true);
      const res = await api.post("/todos", {
        ...data,
        assignedBy: user?.uid,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Create notification for the assigned user
      if (data.assignedTo) {
        await api.post("/notifications", {
          userId: data.assignedTo,
          role: data.role,
          title: "New Todo Assigned",
          message: `You have been assigned a new todo: ${data.title}`,
          linkText: "View Todo",
          link: `/todo`,
          type: "todo-assigned",
          refId: res.data_id,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // Send email to admin with properly formatted data
      await api.post(`${API_BASE_URL}/mail/assigned-todo-email`, {
        data: {
          ...data,
          priority: data.priority,
          assignedTo: data.assignedTo
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Task created successfully!");
      queryClient.invalidateQueries(["todos"]);
      setOpenModal(false);
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error(
        error?.response?.data?.message
          ? error?.response?.data?.message
          : "Task not added"
      );
    } finally {
      setLoading(false);
    }
  };

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.79)",
      zIndex: 100,
    },
  };

  return (
    <Modal
      isOpen={openModal}
      onRequestClose={() => setOpenModal(false)}
      style={customStyles}
      contentLabel="Example Modal"
    >
      <div className="md:w-[500px] w-full">
        <h1 className="text-[#000] font-semibold text-[24px]">Add Todo</h1>

        <div className="mt-3 w-full">
          <input
            type="text"
            placeholder="Enter Todo Title"
            className="px-3 py-2 my-3 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.45)]"
            name="title"
            value={data.title}
            onChange={handleInputChange}
          />
          <input
            type="date"
            placeholder="Enter Todo Date"
            className="px-3 py-2 my-3 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.45)]"
            name="date"
            value={data.date}
            onChange={handleInputChange}
          />
          <input
            type="date"
            placeholder="Enter Due Date"
            className="px-3 py-2 my-3 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.45)]"
            name="dueDate"
            value={data.dueDate}
            onChange={handleInputChange}
          />
          <Select
            labelInValue
            options={users?.map((e) => {
              return { label: e?.name, value: e?.id, role: e?.role };
            })}
            onChange={(e) => {
              const selectedUser = users?.find(user => user.id === e.value);
              setData({
                ...data,
                assignedTo: e.value,
                role: selectedUser?.role
              });
            }}
            className="w-full"
            size="large"
            placeholder="Assign to"
          />
          <Select
            options={[
              { label: "Urgent", value: "Urgent" },
              { label: "Medium", value: "Medium" },
              { label: "Normal", value: "Normal" },
            ]}
            onChange={(value) => setData({ ...data, priority: value })}
            className="w-full mt-2"
            placeholder="Priority"
            size="large"
          />
          <textarea
            type="text"
            placeholder="Enter Todo Description"
            className="px-3 py-2 my-3 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.45)]"
            name="description"
            value={data.description}
            onChange={handleInputChange}
          />

          <div className="flex justify-center mt-1">
            <button
              type="submit"
              className="py-3 px-8 rounded-full bg-[#053F92] text-white"
              disabled={loading}
              onClick={handleAddData}
            >
              {loading ? "Adding..." : "Add Todo"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddTodoModal;